import { getCurrentInstance, markRaw, onBeforeUnmount, reactive } from "vue";
import type { DocumentSpaceQuery } from "@/graphql/operations";

export type DeleteItemOperationResult = {
  error?: unknown;
};

export type ItemDeletionResult = "ignored" | "success" | "error" | "pending";

export type ItemReconciliationResult = {
  itemIds: ReadonlySet<string>;
  error?: unknown;
};

export type DeleteReconciledItem = (
  itemId: string,
  executeDelete: () => PromiseLike<DeleteItemOperationResult>,
) => Promise<ItemDeletionResult>;

export type IsItemDeletionPending = (itemId: string) => boolean;

type ItemDeletionState = {
  scopeId: string;
  itemId: string;
  phase: "deleting" | "awaiting-removal" | "awaiting-retry";
  minimumVersion: number;
  notificationPending: boolean;
};

type ReconciliationOwner = {
  token: symbol;
  reconcileItems: () => PromiseLike<ItemReconciliationResult>;
  onDeletionConfirmed?: (itemId: string) => void;
  invalidated: boolean;
  invalidationListeners: Set<() => void>;
  invalidate: () => void;
};

type ReconciliationExecution = {
  version: number;
  result?: ItemReconciliationResult;
};

type ScopeControl = {
  version: number;
  owner?: ReconciliationOwner;
  active?: Promise<ReconciliationExecution>;
  completions: Set<string>;
};

export type ReconciledItemDeletionStore = {
  itemStates: Map<string, ItemDeletionState>;
  scopeControls: Map<string, ScopeControl>;
};

export const createReconciledItemDeletionStore =
  (): ReconciledItemDeletionStore =>
    reactive({
      itemStates: new Map<string, ItemDeletionState>(),
      scopeControls: markRaw(new Map<string, ScopeControl>()),
    });

const sharedStore = createReconciledItemDeletionStore();

const stateKey = (scopeId: string, itemId: string) => `${scopeId}:${itemId}`;

const scopeHasItems = (store: ReconciledItemDeletionStore, scopeId: string) =>
  [...store.itemStates.values()].some((state) => state.scopeId === scopeId);

export const documentSpaceItemIds = (
  documentSpace?: DocumentSpaceQuery["documentSpace"] | null,
): ReadonlySet<string> => {
  const itemIds = new Set<string>();

  for (const section of documentSpace?.documentSpaceSections.nodes ?? []) {
    for (const item of section?.documentSpaceItemsBySectionId.nodes ?? []) {
      if (item?.id) {
        itemIds.add(item.id);
      }
    }
  }

  return itemIds;
};

export const useReconciledItemDeletion = ({
  scopeId,
  reconcileItems,
  onDeletionConfirmed,
  store = sharedStore,
}: {
  scopeId: string;
  reconcileItems: () => PromiseLike<ItemReconciliationResult>;
  onDeletionConfirmed?: (itemId: string) => void;
  store?: ReconciledItemDeletionStore;
}) => {
  let control = store.scopeControls.get(scopeId);
  if (!control) {
    control = {
      version: 0,
      completions: new Set(),
    };
    store.scopeControls.set(scopeId, control);
  }

  const cleanupControl = () => {
    if (
      control.owner ||
      control.active ||
      control.completions.size > 0 ||
      scopeHasItems(store, scopeId)
    ) {
      return;
    }

    store.scopeControls.delete(scopeId);
  };

  const deliverCompletions = () => {
    const listener = control.owner?.onDeletionConfirmed;
    if (!listener) {
      return;
    }

    for (const itemId of control.completions) {
      control.completions.delete(itemId);
      listener(itemId);
    }
  };

  const processResult = (version: number, result: ItemReconciliationResult) => {
    if (result.error) {
      return;
    }

    for (const [key, state] of store.itemStates) {
      if (
        state.scopeId !== scopeId ||
        state.phase === "deleting" ||
        version < state.minimumVersion
      ) {
        continue;
      }

      if (
        state.phase === "awaiting-removal" &&
        result.itemIds.has(state.itemId)
      ) {
        continue;
      }

      store.itemStates.delete(key);
      if (state.notificationPending) {
        control.completions.add(state.itemId);
      }
    }

    deliverCompletions();
    cleanupControl();
  };

  const executeReconciliation = async (): Promise<ReconciliationExecution> => {
    while (true) {
      const owner = control.owner;
      if (!owner) {
        return { version: control.version };
      }

      const version = ++control.version;
      let removeInvalidationListener = () => {};
      const invalidated = new Promise<{ type: "invalidated" }>((resolve) => {
        const listener = () => resolve({ type: "invalidated" });
        if (owner.invalidated) {
          listener();
          return;
        }

        owner.invalidationListeners.add(listener);
        removeInvalidationListener = () =>
          owner.invalidationListeners.delete(listener);
      });
      const outcome = await Promise.race([
        Promise.resolve(owner.reconcileItems()).then(
          (result) => ({ type: "result" as const, result }),
          (error) => ({
            type: "result" as const,
            result: { itemIds: new Set<string>(), error },
          }),
        ),
        invalidated,
      ]).finally(removeInvalidationListener);
      if (
        outcome.type === "invalidated" ||
        control.owner?.token !== owner.token
      ) {
        continue;
      }

      processResult(version, outcome.result);
      return { version, result: outcome.result };
    }
  };

  const refreshItems = ({ fresh = false } = {}) => {
    if (control.active && !fresh) {
      return control.active;
    }

    const previous = control.active;
    const execution = (
      previous
        ? previous.catch(() => ({ version: control.version }))
        : Promise.resolve({ version: control.version })
    ).then(executeReconciliation);
    control.active = execution;
    void execution.finally(() => {
      if (control.active === execution) {
        control.active = undefined;
      }
      cleanupControl();
    });
    return execution;
  };

  control.owner?.invalidate();
  let owner!: ReconciliationOwner;
  owner = {
    token: Symbol(scopeId),
    reconcileItems,
    onDeletionConfirmed,
    invalidated: false,
    invalidationListeners: new Set(),
    invalidate: () => {
      if (owner.invalidated) {
        return;
      }

      owner.invalidated = true;
      for (const listener of owner.invalidationListeners) {
        listener();
      }
      owner.invalidationListeners.clear();
    },
  };
  // Each Classic route mounts one query-owning editor per Space.
  // A keyed remount replaces that owner; browser tabs have separate stores.
  control.owner = owner;
  deliverCompletions();

  if (
    getCurrentInstance() &&
    !control.active &&
    [...store.itemStates.values()].some(
      (state) => state.scopeId === scopeId && state.phase !== "deleting",
    )
  ) {
    void refreshItems({ fresh: true });
  }

  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (control.owner?.token === owner.token) {
        owner.invalidate();
        control.owner = undefined;
      }
      cleanupControl();
    });
  }

  const isItemDeletionPending: IsItemDeletionPending = (itemId) =>
    store.itemStates.has(stateKey(scopeId, itemId));

  const deleteItem: DeleteReconciledItem = async (itemId, executeDelete) => {
    const key = stateKey(scopeId, itemId);
    if (store.itemStates.has(key)) {
      return "ignored";
    }

    store.itemStates.set(key, {
      scopeId,
      itemId,
      phase: "deleting",
      minimumVersion: Infinity,
      notificationPending: false,
    });

    let mutationResult: DeleteItemOperationResult;
    try {
      mutationResult = await executeDelete();
    } catch (error) {
      store.itemStates.set(key, {
        scopeId,
        itemId,
        phase: "awaiting-retry",
        minimumVersion: control.version + 1,
        notificationPending: false,
      });
      await refreshItems({ fresh: true });
      throw error;
    }

    store.itemStates.set(key, {
      scopeId,
      itemId,
      phase: mutationResult.error ? "awaiting-retry" : "awaiting-removal",
      minimumVersion: control.version + 1,
      notificationPending:
        !mutationResult.error && Boolean(onDeletionConfirmed),
    });
    await refreshItems({ fresh: true });

    if (mutationResult.error) {
      return "error";
    }

    return store.itemStates.has(key) ? "pending" : "success";
  };

  return {
    deleteItem,
    isItemDeletionPending,
    refreshItems,
  };
};
